import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { eMarketType } from '../markets/eMarketName';
import { MarketsFactoryService } from '../markets/markets-factory.service';
import { NetworksService } from '@services/networks/networks.service';

export interface IPrice
{
    price: string;
    price_change: string;
}

@Injectable({
    providedIn: 'root',
})
export class PricesService
{
    private currencyMarketData: { [ key: string ]: ReplaySubject<IPrice>; } = {};

    private prices: {
        [ key: string ]: { [ fiat: string ]: ReplaySubject<IPrice>; };
    } = {};

    private http: HttpClient;
    private marketService: MarketsFactoryService;
    private networkService: NetworksService

    private pricesData: ReplaySubject<{
        [ address: string ]: { type: string;[ key: string ]: any; };
    }> = new ReplaySubject(1);

    private readonly DEFAULT_PRICE: IPrice = { price: '0', price_change: null };

    constructor(private injector: Injector)
    {
        this.http = this.injector.get(HttpClient);
        this.marketService = this.injector.get(MarketsFactoryService);
        this.networkService = this.injector.get(NetworksService);

        this.loadPricesData();
    }
    //ref getPrice
    public async getPrice(
        _symbol: string,
        _fiat: string,
        _chainId: number,
        _contractAddress?: string
    ): Promise<{
        price: string;
        price_change: string;
    }>
    {
        const symbol = _symbol.toUpperCase();
        const fiat = _fiat.toLowerCase();
        const contractAddress = !!_contractAddress
            ? _contractAddress.toLowerCase()
            : null;
        const key = (contractAddress || _chainId.toString()).toLowerCase();

        if (!!this.prices[ key ]?.[ fiat ])
        {
            return this.prices[ key ][ fiat ].pipe(take(1)).toPromise();
        }

        if (!this.prices[ key ]) this.prices[ key ] = {};

        this.prices[ key ][ fiat ] = new ReplaySubject(1);

        const priceSource = await this.pricesData.pipe(take(1)).toPromise();

        const price: IPrice = await this.getMarketData(
            symbol,
            fiat,
            _chainId,
            contractAddress,
            priceSource[ key ]
        );

        this.prices[ key ][ fiat ].next(price);

        return this.prices[ key ][ fiat ].pipe(take(1)).toPromise();
    }

    private async getMarketData(
        _symbol: string,
        _fiat: string,
        _chainId: number,
        _contractAddress?: string,
        _priceSource?: any
    ): Promise<IPrice>
    {

        try
        {
            const network = await this.networkService.getBlockchainByChainId(_chainId);

            if (network.testnet)
            {
                return this.DEFAULT_PRICE;
            }

            const price = await this.http.post<IPrice>(`${ environment.priceServiceHost }/api/price`, { chain_id: _chainId, contract_address: _contractAddress }).toPromise();

            return price;
        } catch (error)
        {
            console.error(error);
            return this.DEFAULT_PRICE;
        }
    }

    private async getCoingeckoData(
        _symbol: string,
        _fiat: string,
        _chainId: number,
        _contractAddress?: string
    ): Promise<IPrice>
    {
        try
        {
            const url = this.getCoingeckoUrl(_symbol, _chainId, _contractAddress);
            const response = await this.http.get(url).toPromise();

            if (Boolean(response[ 'error' ]))
            {
                return this.DEFAULT_PRICE;
            } else
            {
                return {
                    price: response[ 'market_data' ][ 'current_price' ][ _fiat ],
                    price_change:
                        response[ 'market_data' ][
                        'price_change_percentage_24h_in_currency'
                        ][ _fiat ],
                };
            }
        } catch (e)
        {
            return this.DEFAULT_PRICE;
        }
    }

    private getCoingeckoUrl(_symbol: string, _chainId: number, _contract?: string): string
    {
        // to environment
        // const coinsIds = {
        //     [eBlockchains.ETHEREUM]: 'ethereum',
        //     [eBlockchains.POLYGON]: 'matic-network',
        //     [eBlockchains.BSC]: 'binancecoin'
        // }

        // const tokensIds = {
        //     [eBlockchains.ETHEREUM]: 'ethereum',
        //     [eBlockchains.POLYGON]: 'polygon-pos',
        //     [eBlockchains.BSC]: 'binance-smart-chain'
        // }
        const coinsId = environment[ _chainId ]?.coinsId;
        const tokensId = environment[ _chainId ]?.tokensId;

        if (Boolean(_contract))
        {
            return `https://api.coingecko.com/api/v3/coins/${ tokensId }/contract/${ _contract }`;
        }

        return `https://api.coingecko.com/api/v3/coins/${ coinsId }?tickers=false&community_data=false&developer_data=false`;

    }

    private async getExchangeData(
        _symbol: string,
        _market: string,
        _pair: string
    ): Promise<IPrice>
    {
        const market = await this.marketService.getMarket(eMarketType[ _market ]);

        if (!_market)
        {
            return this.DEFAULT_PRICE;
        }

        try
        {
            const prices = await market.getTicker(_pair);

            return {
                price: prices.sell,
                price_change: '0',
            };
        } catch (e)
        {
            return this.DEFAULT_PRICE;
        }
    }


    private async getGeckoterminalData(
        _token: string
    ): Promise<IPrice>
    {
        const data = await this.http.get<any>(`/api/price/geckoterminal/${ _token }`).toPromise();

        if (!data)
        {
            return this.DEFAULT_PRICE;
        }

        return {
            price: data?.data?.attributes?.pools?.[ 0 ]?.price_in_usd,
            price_change: '0',
        };
    }

    private async getKaizenData(
        _endpoint: string,
        _key: string
    ): Promise<IPrice>
    {
        try
        {
            const response = await this.http
                .get(`https://kaizen.finance${ _endpoint }`)
                .toPromise();

            return {
                price: response[ _key ],
                price_change: null,
            };
        } catch (e)
        {
            return this.DEFAULT_PRICE;
        }
    }

    private async loadPricesData()
    {
        const response = <any>(
            await this.http
                .get(`/assets/data/prices.json?t=${ Date.now() }`)
                .toPromise()
        );

        this.pricesData.next(response);
    }
}
